import { agentCoreClient, lambdaClient } from '../config/http.client';

// Re-export clients for backward compatibility
export const agentcoreClient = agentCoreClient;
export const lambdaClientExport = lambdaClient;

// ==================== AgentCore APIs (Report Generation) ====================

/**
 * Generate report using AgentCore
 */
export const generateReport = async (backend, idea, advancedMode, workflowType = 'sequential', modelId = null, questionnaireData = null) => {
  const payload = {
    action: 'generate-report',
    idea: idea,  // Backend expects 'idea' not 'prompt'
    backend: backend,
    advanced_mode: advancedMode,
    workflow_type: workflowType,
    use_cache: true, // Enable intelligent caching (checks for exact match with same settings)
    model_id: modelId,  // Pass the selected model ID to AgentCore
    questionnaire_data: questionnaireData  // NEW: Structured 8Q data for better prompts
  };

  const response = await agentcoreClient.post('/agentcore/invoke', payload);
  const jobData = response.data;

  if (jobData.status === 'accepted') {
    // Return job info immediately instead of polling
    // User can check job status in the Jobs tab
    return {
      job_id: jobData.job_id,
      status: 'accepted',
      message: 'Report generation started. Check the Jobs tab for progress.'
    };
    
    // COMMENTED OUT: Automatic polling - now handled via Jobs tab
    // return pollForJobResults(jobData.job_id, jobData.poll_interval || 10000);
  } else {
    throw new Error(jobData.error || 'Failed to start job');
  }
};

// COMMENTED OUT: Polling function - now jobs are checked via Jobs tab
/*
const pollForJobResults = async (jobId, interval = 10000, maxAttempts = 15) => {
  // Wait initial 30s before the first poll to give the job time to initialize
  await new Promise(resolve => setTimeout(resolve, 30000));

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      // Use the new job status endpoint
      const response = await agentcoreClient.get(`/agentcore/jobs/${jobId}`);
      const jobStatus = response.data;
      
      console.log(`Job ${jobId}: ${jobStatus.status} (${jobStatus.progress}%)`);
      
      if (jobStatus.status === 'completed') {
        return jobStatus.result;
      } else if (jobStatus.status === 'failed') {
        throw new Error(jobStatus.error || 'Job failed');
      } else if (jobStatus.status === 'processing' || jobStatus.status === 'queued') {
        // Continue polling
        await new Promise(resolve => setTimeout(resolve, interval));
      } else {
        throw new Error(`Unknown job status: ${jobStatus.status}`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Job not found');
      }
      if (attempt === maxAttempts - 1) {
        throw error;
      }
      // await new Promise(resolve => setTimeout(resolve, interval));
    }
  }
  
  throw new Error('Job polling timed out after 3 minutes');
};
*/

/**
 * Get job status by ID
 */
export const getJobStatus = async (jobId) => {
  const response = await agentcoreClient.get(`/agentcore/jobs/${jobId}`);
  return response.data;
};

/**
 * Get all jobs
 * @param {string} type - Optional job type filter: 'idea_analysis', 'code_generation', or null for all
 */
export const getAllJobs = async (type = null) => {
  const params = type ? { type } : {};
  const response = await agentcoreClient.get('/agentcore/jobs', { params });
  return response.data;
};

/**
 * Delete a job
 */
export const deleteJob = async (jobId) => {
  const response = await agentcoreClient.delete(`/agentcore/jobs/${jobId}`);
  return response.data;
};

/**
 * Get models configuration - Now fetched from Lambda (database layer)
 */
export const getModelsConfig = async () => {
  const response = await lambdaClient.get('/models/config');
  return response.data;
};

/**
 * Get available backends
 */
export const getBackends = async () => {
  const response = await agentcoreClient.get('/backends');
  return response.data;
};

/**
 * Get agents list
 */
export const getAgentsList = async () => {
  const response = await agentcoreClient.get('/agents');
  return response.data;
};

/**
 * Health check for AgentCore
 */
export const healthCheck = async () => {
  const response = await agentcoreClient.get('/health');
  return response.data;
};

// ==================== Lambda APIs (Database Operations) ====================

/**
 * Save report to database
 */
export const saveReport = async (reportData) => {
  const response = await lambdaClient.post('/reports', {
    report_data: reportData
  });
  return response.data;
};

/**
 * Get report history
 */
export const getReportHistory = async (limit = 20) => {
  const response = await lambdaClient.get('/reports', {
    params: { limit }
  });
  return response.data;
};

/**
 * Get report by ID
 */
export const getReportById = async (reportId) => {
  const response = await lambdaClient.get(`/reports/${reportId}`);
  return response.data;
};

/**
 * Delete report
 */
export const deleteReport = async (reportId) => {
  const response = await lambdaClient.delete(`/reports/${reportId}`);
  return response.data;
};

/**
 * Toggle favorite status
 */
export const toggleFavorite = async (reportId, isFavorite) => {
  const response = await lambdaClient.put(`/reports/${reportId}/favorite`, {
    is_favorite: isFavorite
  });
  return response.data;
};

/**
 * Get favorite reports
 */
export const getFavoriteReports = async (limit = 20) => {
  const response = await lambdaClient.get('/reports/favorites', {
    params: { limit }
  });
  return response.data;
};

/**
 * Search for exact and similar reports (cache lookup)
 */
export const searchReports = async (query, mode = 'standard', backendType = null) => {
  const response = await lambdaClient.post('/reports/search', {
    query: query,
    mode: mode,
    backend_type: backendType,
    similarity_threshold: 0.3,  // Lower threshold for better fuzzy matching
    max_results: 5,
    ttl_hours: 24
  });
  return response.data;
};

/**
 * Get database statistics
 */
export const getDatabaseStats = async (days = 7) => {
  const response = await lambdaClient.get('/reports/stats', {
    params: { days }
  });
  return response.data;
};

// Export all services
const agentcoreService = {
  // Report generation
  generateReport,
  getModelsConfig,
  getBackends,
  getAgentsList,
  healthCheck,
  getJobStatus,
  getAllJobs,
  deleteJob,
  
  // Database operations
  saveReport,
  getReportHistory,
  getReportById,
  deleteReport,
  toggleFavorite,
  getFavoriteReports,
  searchReports,
  getDatabaseStats,
};

export default agentcoreService;
